import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const AccountDropdown = () => {
  const { user, logout, isAdmin, isAdminOrStaff } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  const menuItems = [
    ...(isAdminOrStaff()
      ? [
          {
            icon: ChartBarIcon,
            label: 'Admin Dashboard',
            path: '/admin',
            color: 'text-amber-600',
          },
        ]
      : []),
    {
      icon: UserCircleIcon,
      label: 'My Profile',
      path: '/profile',
      color: 'text-blue-600',
    },
    {
      icon: ShoppingBagIcon,
      label: 'My Orders',
      path: '/my-orders',
      color: 'text-green-600',
    },
    {
      icon: CalendarIcon,
      label: 'My Bookings',
      path: '/my-bookings',
      color: 'text-purple-600',
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Account Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Account menu"
        className="ml-3 text-white hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1"
      >
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-full sm:mt-2 w-auto sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 animate-fadeIn max-h-[calc(100vh-5rem)] overflow-y-auto">
          {/* User Info Header */}
          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            <span
              className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                user?.role === 'admin'
                  ? 'bg-red-100 text-red-800'
                  : user?.role === 'staff'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {user?.role?.toUpperCase()}
            </span>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center px-3 sm:px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors gap-3"
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${item.color}`} />
                <span className="truncate font-medium">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-200 pt-1">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 sm:px-4 py-3 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors gap-3"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDropdown;
